import { clearSessionData } from "./clearSessionData";
import { peekPostAuthRedirect, setPostAuthRedirect } from "../storage/postAuthRedirect";
import { userSetupPath } from "../../app/routes/routes";
import { getAccountMessages } from "../../shared/utils/translations/accountMessages";

export const SESSION_EXPIRED_KEY = "sessionExpired";
// El aviso se pinta en el idioma de la interfaz. `clearSessionData` conserva la
// preferencia de idioma al limpiar la sesión, así que sigue disponible en el
// momento de mostrarlo. Quien no puede leer LangContext -- SessionExpirationBoundary
// monta por encima de los providers -- pasa `readStoredInterfaceLanguage()`.
export const getSessionExpiredMessage = (language) =>
  getAccountMessages(language).sessionExpired;

// Texto literal que regresa ValidarUsuarioMiddleware (duplicado en varios microservicios)
// cuando Estatus <> 'A' en proUsuarios -- ver uspDatosComplementarios. NO es un token
// invalido/vencido: el token sigue siendo valido, solo falta terminar el alta. Tratarlo
// como "sesion expirada" es enganoso y ademas no manda a la persona a donde puede
// resolverlo (/user_setup). PrivateRoute ya cubre el caso principal (login fresco); esto
// es la red de seguridad para una pestana/sesion ya abierta que se queda sin completar.
const INACTIVE_USER_BACKEND_ERROR = "Usuario inactivo o eliminado.";

let expired = false;
const listeners = new Set();
export const isSessionExpiring = () => expired;
export const subscribeSessionExpiration = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const AUTH_PAGES = ["/", "/login", "/singup", "/recover_password", "/verify_email"];

// La aplicacion monta HashRouter, asi que la ruta vive DENTRO del hash
// ("#/guides2/30/FREE?from=contratar") y `window.location.pathname` vale siempre
// "/". Leyendo el pathname, toda pantalla parecia una de autenticacion, el
// destino no se guardaba nunca y quien perdia la sesion leyendo una guia volvia
// al feed en vez de a la guia. Se conserva la lectura por pathname como
// respaldo, para una URL que no traiga hash de router.
const readCurrentRoute = () => {
  const { pathname, search, hash } = window.location;
  if (hash.startsWith("#/")) return hash.slice(1);
  return `${pathname}${search}${hash}`;
};

// Solo la parte de ruta, sin query ni fragmento, que es lo unico comparable
// contra AUTH_PAGES.
const routePathOf = (route) => route.split(/[?#]/)[0].replace(/\/+$/, "") || "/";

export const expireSession = () => {
  if (expired) return;
  expired = true;
  const route = readCurrentRoute();
  const isAuthPage = AUTH_PAGES.includes(routePathOf(route));
  const destination = isAuthPage ? peekPostAuthRedirect() : route;
  clearSessionData();
  if (destination) setPostAuthRedirect(destination);
  sessionStorage.setItem(SESSION_EXPIRED_KEY, "true");
  listeners.forEach((listener) => listener());
};

// No limpia token/sesion ni cierra Firebase ni recarga la pagina completa: la cuenta
// sigue siendo valida, solo le falta terminar el alta. Un window.location.replace con
// una ruta "plana" (sin '#') recargaria la SPA en una URL que HashRouter no reconoce
// (la ruta vive DENTRO del hash) y, si el 401 se repite ahi mismo mientras el usuario
// esta legitimamente completando su perfil, entra en un loop de recargas -- por eso
// esto es solo un cambio de hash (sin red, sin remount) e idempotente si ya se esta ahi.
export const redirectToUserSetup = () => {
  if (expired) return;
  const target = `#${userSetupPath}`;
  if (window.location.hash === target) return;
  window.location.hash = userSetupPath;
};

export const handleSessionResponseError = (error) => {
  const token = localStorage.getItem("token");
  const config = error.config || {};
  const path = new URL(config.url || "", window.location.origin).pathname.replace(/\/$/, "");
  const isLogin = path === "/api/sec/login" || path === "/api/sec/login/externo";
  const authorization = config.headers?.Authorization || config.headers?.authorization;
  // Ignore anonymous/login failures and responses belonging to an older session.
  if (error.response?.status === 401 && !isLogin && token && authorization === `Bearer ${token}`) {
    const backendMessage = error.response?.data?.error;
    if (backendMessage === INACTIVE_USER_BACKEND_ERROR) {
      redirectToUserSetup();
    } else {
      expireSession();
    }
  }
  return Promise.reject(error);
};
